import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ChangeHistory } from "@/common/components/ChangeHistory";
import { AddressSection } from "@/features/privateCompanyLeadsFunnel/components/AddressSection";
import { Checklist } from "@/features/privateCompanyLeadsFunnel/components/Checklist";
import { ContactsSection } from "@/features/privateCompanyLeadsFunnel/components/ContactsSection";
import { GeneralInformationSection } from "@/features/privateCompanyLeadsFunnel/components/GeneralInformationSection";
import { GenericDocumentsSection } from "@/features/privateCompanyLeadsFunnel/components/GenericDocumentsSection";
import { LeadHeader } from "@/features/privateCompanyLeadsFunnel/components/LeadHeader";
import { OperationalInformationSection } from "@/features/privateCompanyLeadsFunnel/components/OperationalInformationSection";
import { ResponsibleSection } from "@/features/privateCompanyLeadsFunnel/components/ResponsibleSection";
import { stateStrategy, translateStateStrategy } from "@/features/privateCompanyLeadsFunnel/constants/stateStrategy.constant";
import useAdvancePrivateCompanyLead from "@/features/privateCompanyLeadsFunnel/hooks/useAdvancePrivateCompanyLead";
import usePrivateCompanyLeadForm from "@/features/privateCompanyLeadsFunnel/hooks/usePrivateCompanyLeadForm";
import { LeadFormStateProps } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead";
import { FormProvider } from "react-hook-form";
import { SpecialFees } from "../SpecialFees";

const LeadQuotingFinalizationForm = ({ id, privateCompanyLead }: LeadFormStateProps) => {
  const {
    serverError,
    formMethods,
    passedEveryRequirement,
    redirectOnSuccess,
    onError,
    submit,
    submitManagers,
    updatePrivateCompanyLeadManagersIsPending
  } = usePrivateCompanyLeadForm({ privateCompanyLead });

  const {
    control,
    register,
    formState: { errors },
  } = formMethods;

  const {
    mutate: advancePrivateCompanyLead,
    isPending: advancePrivateCompanyLeadIsPending
  } = useAdvancePrivateCompanyLead({
    id,
    onSuccess: () => redirectOnSuccess('/private-clients'),
    onError
  });

  return (
    <FormProvider {...formMethods}>
      <form>
        <LeadHeader
          privateCompanyLeadId={id}
          display_name={privateCompanyLead.display_name}
          url={privateCompanyLead.general_information?.company_logo?.url}
          showArchiveButton
          archived_at={privateCompanyLead.archived_at}
          buttons={
            <Button
              type="button"
              className={passedEveryRequirement ? "shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" : ''}
              variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
              disabled={!passedEveryRequirement && !advancePrivateCompanyLeadIsPending}
              onClick={() => advancePrivateCompanyLead()}
              isLoading={advancePrivateCompanyLeadIsPending}
            >
              Enviar para implantação
            </Button>
          }
        />
        <div className='flex gap-6'>
          <Box className="w-full flex flex-col gap-6 h-[calc(100vh-130px)] overflow-y-scroll shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            {serverError && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, tente novamente</p>}
            <ResponsibleSection
              privateCompanyLead={privateCompanyLead}
              submit={submitManagers}
              submitObservations={submit}
              disabled={advancePrivateCompanyLeadIsPending || updatePrivateCompanyLeadManagersIsPending}
            />
            <OperationalInformationSection
              privateCompanyLeadId={id}
              operational_information={privateCompanyLead?.operational_information}
              financial_information={privateCompanyLead?.financial_information}
              contract={privateCompanyLead?.contract}
              control={control}
              register={register}
              errors={errors}
              isToAttachContract={!privateCompanyLead.contract?.file?.url}
              disabled
            />
            <SpecialFees
              control={control}
              errors={errors}
              fee_rule={privateCompanyLead.fee_rule}
              disabled
            />
            <GeneralInformationSection
              general_information={privateCompanyLead?.general_information}
              register={register}
              errors={errors}
              disabled
            />
            <AddressSection
              address={privateCompanyLead?.address}
              errors={errors}
              disabled
            />
            <ContactsSection
              privateCompanyLeadId={id}
              leadState={privateCompanyLead?.state}
              contacts={privateCompanyLead?.contacts}
              contact_for_master_user={privateCompanyLead?.contact_for_master_user}
              control={control}
            />
            <GenericDocumentsSection
              privateCompanyLeadId={id}
              generic_documents={privateCompanyLead.generic_documents}
              control={control}
              disabled
            />
          </Box>
          <div className="flex flex-col gap-4">
            <Checklist
              requirement_list_summary={privateCompanyLead.requirement_list_summary}
              state={translateStateStrategy[stateStrategy[privateCompanyLead.state as keyof typeof stateStrategy]]}
              className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
              Buttons={() => (
                <Button
                  type="button"
                  variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                  disabled={!passedEveryRequirement && !advancePrivateCompanyLeadIsPending}
                  onClick={() => advancePrivateCompanyLead()}
                  isLoading={advancePrivateCompanyLeadIsPending}
                >
                  Enviar para implantação
                </Button>
              )}
            />
            <ChangeHistory logs={privateCompanyLead?.logs || []} entityId={id} />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default LeadQuotingFinalizationForm;
